/**
 * Khu vực M3: Phố chợ, Bãi biển, Chợ đêm.
 *
 * - Mở khu theo cấp người chơi (1 / 3 / 6); Chợ đêm chỉ mở buổi tối (18–24 giờ game).
 * - Chi nhánh 2 (Bãi biển) / 3 (Chợ đêm) mở theo cấp cửa hàng 4 / 5; nâng cấp
 *   lên cấp đó cần người chơi đạt cấp mở khu tương ứng.
 * - Lưu lượng: tổng theo giờ trong ngày được tính từ bảng "lưu lượng theo cấp
 *   và khu" — trung bình ngày KHỚP hệ số lưu lượng sheet "Cấp cửa hàng"
 *   (validator kiểm), nên nhịp cân bằng M1/M2 không đổi; chỉ phân bổ lại
 *   theo giờ (Chợ đêm dồn vào buổi tối).
 * - Chợ đêm ít nhạy giá khi Đắt: hệ số cầu Đắt nhân thêm heSoCauDat của khu,
 *   bình quân gia quyền theo lưu lượng từng giờ.
 */
import { gioTrongNgay } from './khach.js';
import type { DuLieuGame, KhuVucM3, TrangThaiGame } from './types.js';

/** Tìm khu theo mã. Ném lỗi nếu mã lạ. */
export function layKhuVuc(duLieu: DuLieuGame, ma: string): KhuVucM3 {
  const khu = duLieu.khuVuc.khuVuc.find((k) => k.ma === ma);
  if (!khu) throw new Error(`Mã khu lạ: ${ma}.`);
  return khu;
}

/** Các khu đã mở theo cấp người chơi (chưa xét giờ mở cửa). */
export function khuDaMo(state: TrangThaiGame, duLieu: DuLieuGame): KhuVucM3[] {
  return duLieu.khuVuc.khuVuc.filter((k) => state.nguoiChoi.cap >= k.capNguoiChoiMo);
}

/** Khu có đang mở cửa tại một giờ trong ngày không (Chợ đêm 18–24). */
export function khuMoTheoGio(khu: KhuVucM3, gio: number): boolean {
  return gio >= khu.gioMoDau && gio < khu.gioMoCuoi;
}

/** Các khu đã mở VÀ đang mở cửa tại giờ hiện tại của state. */
export function khuDangMo(state: TrangThaiGame, duLieu: DuLieuGame): KhuVucM3[] {
  const gio = gioTrongNgay(state.tongGioGame);
  return khuDaMo(state, duLieu).filter((k) => khuMoTheoGio(k, gio));
}

/** Chi nhánh ở khu này đã mở chưa (theo cấp cửa hàng). */
export function chiNhanhDaMo(state: TrangThaiGame, khu: KhuVucM3): boolean {
  return state.capCuaHang >= khu.capCuaHangMoChiNhanh;
}

/** Số giờ mở cửa mỗi ngày của khu. */
export function soGioMo(khu: KhuVucM3): number {
  return khu.gioMoCuoi - khu.gioMoDau;
}

/** Phần lưu lượng của một khu ở cấp cửa hàng hiện tại (trung bình ngày). */
export function luuLuongKhuTheoCap(
  state: TrangThaiGame,
  khu: KhuVucM3,
  duLieu: DuLieuGame,
): number {
  const dong = duLieu.khuVuc.luuLuongTheoCap.find((d) => d.cap === state.capCuaHang);
  if (!dong) throw new Error(`Không có dòng lưu lượng cho cấp ${state.capCuaHang}.`);
  if (khu.ma === 'pho-cho') return dong.phoCho;
  if (khu.ma === 'bai-bien') return dong.baiBien;
  if (khu.ma === 'cho-dem') return dong.choDem;
  throw new Error(`Mã khu lạ: ${khu.ma}.`);
}

/**
 * Hệ số lưu lượng theo giờ trong ngày: tổng phần đóng góp của các chi nhánh
 * đang mở cửa, quy về giờ (khu mở 6 giờ/ngày thì dồn gấp 4 khi mở).
 * Trung bình ngày = hệ số lưu lượng của cấp (khớp bảng tính).
 */
export function heSoLuuLuongTheoGio(state: TrangThaiGame, duLieu: DuLieuGame, gio: number): number {
  let tong = 0;
  for (const khu of duLieu.khuVuc.khuVuc) {
    if (state.nguoiChoi.cap < khu.capNguoiChoiMo) continue;
    if (!chiNhanhDaMo(state, khu)) continue;
    if (!khuMoTheoGio(khu, gio)) continue;
    tong += (luuLuongKhuTheoCap(state, khu, duLieu) * 24) / soGioMo(khu);
  }
  return tong;
}

/**
 * Hệ số cầu hiệu dụng theo giờ: khi chính sách Đắt, khách ở khu ít nhạy giá
 * (Chợ đêm) được nhân heSoCauDat, bình quân gia quyền theo lưu lượng giờ đó.
 */
export function heSoCauDatTheoGio(state: TrangThaiGame, duLieu: DuLieuGame, gio: number): number {
  let tu = 0;
  let mau = 0;
  for (const khu of duLieu.khuVuc.khuVuc) {
    if (state.nguoiChoi.cap < khu.capNguoiChoiMo) continue;
    if (!chiNhanhDaMo(state, khu)) continue;
    if (!khuMoTheoGio(khu, gio)) continue;
    const phan = (luuLuongKhuTheoCap(state, khu, duLieu) * 24) / soGioMo(khu);
    tu += phan * khu.heSoCauDat;
    mau += phan;
  }
  return mau > 0 ? tu / mau : 1;
}

/** Cấp người chơi tối thiểu để nâng lên cấp cửa hàng tiếp theo (null = được nâng). */
export function capNguoiChoiToiThieuDeNangCap(state: TrangThaiGame, duLieu: DuLieuGame): number {
  const capTiep = state.capCuaHang + 1;
  const khu = duLieu.khuVuc.khuVuc.find((k) => k.capCuaHangMoChiNhanh === capTiep);
  return khu ? khu.capNguoiChoiMo : 1;
}

/** Lý do không nâng cấp được (null = nâng được). */
export function lyDoKhongNangCapDuoc(state: TrangThaiGame, duLieu: DuLieuGame): string | null {
  const toiThieu = capNguoiChoiToiThieuDeNangCap(state, duLieu);
  if (state.nguoiChoi.cap < toiThieu) {
    const khu = duLieu.khuVuc.khuVuc.find((k) => k.capNguoiChoiMo === toiThieu);
    const tenKhu = khu ? khu.ten : 'khu mới';
    return (
      `Cần đạt cấp người chơi ${toiThieu} để mở ${tenKhu} ` +
      `trước khi lên cấp cửa hàng ${state.capCuaHang + 1}.`
    );
  }
  return null;
}

/**
 * Di chuyển người chơi sang khu khác. Ném lỗi tiếng Việt nếu khu chưa mở
 * hoặc đang đóng cửa (Chợ đêm ban ngày).
 */
export function diChuyen(state: TrangThaiGame, duLieu: DuLieuGame, maKhu: string): void {
  const khu = layKhuVuc(duLieu, maKhu);
  if (state.nguoiChoi.cap < khu.capNguoiChoiMo) {
    throw new Error(
      `${khu.ten} mở khi bạn đạt cấp ${khu.capNguoiChoiMo} (hiện tại cấp ${state.nguoiChoi.cap}).`,
    );
  }
  const gio = gioTrongNgay(state.tongGioGame);
  if (!khuMoTheoGio(khu, gio)) {
    throw new Error(`${khu.ten} chỉ mở từ ${khu.gioMoDau} giờ đến ${khu.gioMoCuoi} giờ.`);
  }
  state.khuHienTai = maKhu;
}

/** Lưu lượng trung bình ngày từng chi nhánh (hiển thị UI). */
export function luuLuongTungChiNhanh(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
): Array<{ ma: string; ten: string; daMo: boolean; luuLuong: number }> {
  return duLieu.khuVuc.khuVuc.map((khu) => ({
    ma: khu.ma,
    ten: khu.ten,
    daMo: chiNhanhDaMo(state, khu),
    luuLuong: chiNhanhDaMo(state, khu) ? luuLuongKhuTheoCap(state, khu, duLieu) : 0,
  }));
}
